import styled, { css, keyframes } from 'styled-components';
import { down, up } from 'styled-breakpoints';

import { TransientProps } from '@common/types';

// Animações do Design System
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const livePulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

export { slideIn, slideUp, fadeIn, livePulse };

// Container principal
type ContainerProps = Partial<
  TransientProps<{
    theaterMode: boolean;
  }>
>;

export const Container = styled.article<ContainerProps>`
  ${({ theme, $theaterMode }) => css`
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - ${theme.layout.header});
    background: ${theme.colors.background[80]};

    ${$theaterMode &&
    css`
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 50;
      background: ${theme.colors.foreground[100]};
      min-height: 100vh;
    `}
  `};
`;

// Header da transmissão
export const BroadcastHeader = styled.header`
  ${() => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.5rem;
    background: white;
    border-bottom: 1px solid #e2e8f0;

    ${down('md')} {
      padding: 0.75rem 1rem;
      flex-wrap: wrap;
    }
  `};
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;

  h2 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    ${down('md')} {
      font-size: 0.875rem;
    }
  }
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

// Grid principal de conteúdo
type ContentGridProps = Partial<
  TransientProps<{
    sidebarCollapsed: boolean;
    theaterMode: boolean;
  }>
>;

export const ContentGrid = styled.div<ContentGridProps>`
  ${({ theme, $sidebarCollapsed, $theaterMode }) => css`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 0.25rem 1.5rem;
    flex: 1;
    /* Margem direita para o painel fixo */
    margin-right: ${$sidebarCollapsed
      ? theme.layout.interactionPanelCollapsed
      : theme.layout.interactionPanel};
    transition: margin-right 0.3s ease, padding 0.3s ease;

    /* Em telas grandes, adiciona padding para centralizar visualmente */
    ${up('4xl')} {
      padding-left: 5%;
      padding-right: 1.5rem;
    }

    ${up('5xl')} {
      padding-left: 10%;
    }

    ${$theaterMode &&
    css`
      padding: 0;
      gap: 0;
      margin-right: 0;
    `}

    ${down('lg')} {
      margin-right: 0;
      padding: 1rem;
      padding-bottom: 5rem; /* Espaço para bottom bar */
    }
  `};
`;

// Área do Player
export const PlayerArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 80rem;

  ${down('md')} {
    gap: 1rem;
  }
`;

// Container do Player de Vídeo
type PlayerContainerProps = Partial<
  TransientProps<{
    theaterMode: boolean;
  }>
>;

export const PlayerContainer = styled.div<PlayerContainerProps>`
  ${({ theme, $theaterMode }) => css`
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    background: ${theme.colors.foreground[100]};
    border-radius: ${theme.radii.lg};
    overflow: hidden;
    box-shadow: ${theme.shadows.lg};

    ${up('lg')} {
      border-radius: ${theme.radii.xl};
    }

    ${$theaterMode &&
    css`
      border-radius: 0;
      aspect-ratio: auto;
      height: 100vh;
      width: 100%;
    `}
  `};
`;

// Badge AO VIVO
export const LiveBadge = styled.div`
  ${({ theme }) => css`
    position: absolute;
    top: 1rem;
    left: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.75rem;
    background: ${theme.colors.error[100]};
    color: white;
    font-size: ${theme.fontSizes.xs};
    font-weight: 600;
    border-radius: ${theme.radii.default};
    z-index: 10;

    &::before {
      content: '';
      width: 0.5rem;
      height: 0.5rem;
      background: white;
      border-radius: ${theme.radii.full};
      animation: ${livePulse} 1.5s ease-in-out infinite;
    }

    ${up('lg')} {
      padding: 0.5rem 1rem;
      font-size: ${theme.fontSizes.sm};
    }
  `}
`;

// Webcam flutuante
export const Webcam = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 5rem;
  height: 3.5rem;
  background: linear-gradient(135deg, #312e81, #581c87);
  border-radius: 0.5rem;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.2);

  ${up('lg')} {
    top: 1rem;
    right: 1rem;
    width: 12rem;
    height: 9rem;
    border-radius: 0.75rem;
  }
`;

// Controles do Player
export const PlayerControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  ${up('lg')} {
    padding: 1.5rem;
  }
`;

export const PlayerControlsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const PlayerControlsRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const PlayerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: 1rem;
    height: 1rem;
  }

  ${up('lg')} {
    width: 2.5rem;
    height: 2.5rem;

    svg {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
`;

// Reações
export const ReactionsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 9999px;

  ${up('lg')} {
    padding: 0.5rem 1rem;
    gap: 0.5rem;
  }
`;

export const ReactionButton = styled.button`
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.2);
  }

  ${up('lg')} {
    font-size: 1.25rem;
  }
`;

// Progress Bar
export const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 0.25rem;
  background: rgba(255, 255, 255, 0.3);
`;

export const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: #6366f1;
  width: ${({ $progress }) => $progress}%;
  transition: width 0.2s linear;
`;

// Info Tabs Container
export const InfoTabsContainer = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.background[100]};
    border: 1px solid ${theme.colors.background[40]};
    border-radius: ${theme.radii.md};
    overflow: hidden;

    ${up('lg')} {
      border-radius: ${theme.radii.lg};
    }
  `}
`;

export const InfoTabsList = styled.div`
  ${({ theme }) => css`
    display: flex;
    border-bottom: 1px solid ${theme.colors.background[40]};
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  `}
`;

type InfoTabProps = Partial<
  TransientProps<{
    active: boolean;
  }>
>;

export const InfoTab = styled.button<InfoTabProps>`
  ${({ theme, $active }) => css`
    flex: 1;
    min-width: fit-content;
    padding: 0.625rem 0.75rem;
    font-size: ${theme.fontSizes.xs};
    font-weight: 500;
    color: ${$active
      ? theme.colors.brand.primary.default
      : theme.colors.foreground[40]};
    background: ${$active ? theme.colors.brand.primary.opaque : 'transparent'};
    border: none;
    border-bottom: 2px solid
      ${$active ? theme.colors.brand.primary.default : 'transparent'};
    cursor: pointer;
    transition: all ${theme.transition.default};
    white-space: nowrap;

    &:hover {
      color: ${theme.colors.brand.primary.default};
      background: ${theme.colors.brand.primary.opaque};
    }

    ${up('lg')} {
      padding: 0.75rem 1rem;
      font-size: ${theme.fontSizes.sm};
    }
  `};
`;

export const InfoTabContent = styled.div`
  ${({ theme }) => css`
    padding: 1rem;
    animation: ${slideIn} 0.3s ease-out forwards;

    ${up('lg')} {
      padding: ${theme.spacing[5]};
    }
  `}
`;

// Bottom Bar Mobile
export const BottomBar = styled.div`
  ${({ theme }) => css`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    background: ${theme.colors.background[100]};
    border-top: 1px solid ${theme.colors.background[40]};
    z-index: 40;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }

    ${up('lg')} {
      display: none;
    }
  `}
`;

type BottomBarItemProps = Partial<
  TransientProps<{
    active: boolean;
  }>
>;

export const BottomBarItem = styled.button<BottomBarItemProps>`
  ${({ theme, $active }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 0.75rem;
    background: ${$active ? theme.colors.brand.primary.opaque : 'transparent'};
    color: ${$active
      ? theme.colors.brand.primary.default
      : theme.colors.foreground[40]};
    border: none;
    border-radius: ${theme.radii.md};
    cursor: pointer;
    transition: all ${theme.transition.default};
    position: relative;
    white-space: nowrap;
    min-width: fit-content;

    svg {
      width: 1.25rem;
      height: 1.25rem;
    }

    span {
      font-size: 0.625rem;
      font-weight: 500;
    }

    &:hover {
      background: ${$active
        ? theme.colors.brand.primary.opaque
        : theme.colors.background[60]};
    }
  `};
`;

export const Badge = styled.span`
  ${({ theme }) => css`
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    min-width: 1.125rem;
    height: 1.125rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.25rem;
    background: ${theme.colors.error[100]};
    color: white;
    font-size: 0.625rem;
    font-weight: 600;
    border-radius: ${theme.radii.full};
  `}
`;

// Drawer Mobile
export const DrawerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 50;
  animation: ${fadeIn} 0.2s ease-out forwards;
`;

export const Drawer = styled.div`
  ${({ theme }) => css`
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    height: 80vh;
    background: ${theme.colors.background[100]};
    border-radius: ${theme.radii.xl} ${theme.radii.xl} 0 0;
    z-index: 51;
    display: flex;
    flex-direction: column;
    animation: ${slideUp} 0.3s ease-out forwards;
  `}
`;

export const DrawerHandle = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: center;
    padding: 0.75rem;
    cursor: grab;

    &::after {
      content: '';
      width: 2.5rem;
      height: 0.25rem;
      background: ${theme.colors.background[40]};
      border-radius: ${theme.radii.full};
    }
  `}
`;

export const DrawerContent = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

// Container para zoom (legado)
export const ContentGridZoom = styled(ContentGrid)``;

// Layout unificado: normal = ContentGrid + PlayerArea, teatro = TheaterModeContainer
type MainLayoutProps = Partial<
  TransientProps<{
    theaterMode: boolean;
    sidebarCollapsed: boolean;
  }>
>;

export const MainLayout = styled.div<MainLayoutProps>`
  ${({ theme, $theaterMode, $sidebarCollapsed }) =>
    $theaterMode
      ? css`
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 100;
          background: ${theme.colors.foreground[100]};
          display: flex;
        `
      : css`
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          padding: 0.25rem 1.5rem;
          flex: 1;
          margin-right: ${$sidebarCollapsed
            ? theme.layout.interactionPanelCollapsed
            : theme.layout.interactionPanel};
          transition: margin-right 0.3s ease, padding 0.3s ease;

          ${down('lg')} {
            margin-right: 0;
            padding: 1rem;
            padding-bottom: 5rem;
          }
        `};
`;

type PlayerSectionProps = Partial<TransientProps<{ theaterMode: boolean }>>;

export const PlayerSection = styled.div<PlayerSectionProps>`
  ${({ $theaterMode }) =>
    $theaterMode
      ? css`
          flex: 1;
          min-width: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        `
      : css`
          display: flex;
          flex-direction: column;
          gap: 1.5rem;

          ${down('md')} {
            gap: 1rem;
          }
        `};
`;

// Wrapper do Player (ancora para Emoji absoluto)
export const PlayerWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  background: #000;
  border-radius: 0.75rem;
  overflow: hidden;
  isolation: isolate;
  --player-controls-padding-right: 5.5rem;
`;

// Modo Teatro
export const TheaterModeContainer = styled.div`
  ${({ theme }) => css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100;
    background: ${theme.colors.foreground[100]};
    display: flex;

    > :first-child {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `}
`;

type TheaterSidebarProps = Partial<TransientProps<{ open: boolean }>>;

export const TheaterSidebar = styled.div<TheaterSidebarProps>`
  ${({ theme, $open }) => css`
    width: ${$open ? '27rem' : '0'};
    height: 100%;
    border-left: ${$open ? `1px solid ${theme.colors.background[40]}` : 'none'};
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: ${theme.colors.background[100]};
    transition: width 0.3s ease;
  `}
`;

export const TheaterInteractionButton = styled.button<
  Partial<TransientProps<{ active: boolean }>>
>`
  ${({ theme, $active }) => css`
    position: absolute;
    bottom: 1rem;
    right: calc(55px + 1rem + 0.5rem);
    z-index: 30;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 55px;
    height: 55px;
    border-radius: ${theme.radii.full};
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: ${$active
      ? theme.colors.brand.primary.default
      : 'rgba(255, 255, 255, 0.12)'};
    backdrop-filter: blur(10px);
    cursor: pointer;
    transition: all 0.25s ease;
    color: white;

    svg {
      width: 1.5rem;
      height: 1.5rem;
    }

    &:hover {
      background: ${$active
        ? theme.colors.brand.primary.default
        : 'rgba(255, 255, 255, 0.22)'};
      transform: scale(1.08);
    }

    &:active {
      transform: scale(0.95);
    }
  `}
`;

export const FloatingChatPanel = styled.div`
  ${({ theme }) => css`
    position: fixed;
    bottom: 6rem;
    right: 1.5rem;
    width: 22rem;
    height: 60vh;
    max-height: 32rem;
    background: ${theme.colors.background[100]};
    border-radius: ${theme.radii.lg};
    box-shadow: ${theme.shadows.xl};
    z-index: 101;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  `}
`;
